import { createSelector } from "@reduxjs/toolkit";
import { IStoreState } from "../../../../reducers/types";
import { getExtraInfo } from "../../../shop/reducer/selectors";
import { parseFlightInfoField } from "../../utils/bookingFields";

/**
 * Flight-info values the traveler entered at checkout (airline + flight number).
 */
export const getFlightInfo = (state: IStoreState) => state.carBook.flightInfo;

/**
 * Normalized `flight-info` booking field parsed off the shop response's
 * (untyped) `bookingFields`. Returns `null` when the provider does not
 * advertise the flight-info field — components additionally gate on the
 * `cars-flight-number-input` feature flag before rendering. `redmond`'s
 * `CarShopResponse` does not type `bookingFields`, so we read it untyped.
 */
export const getFlightInfoFieldSpec = createSelector(
  getExtraInfo,
  (extraInfo) =>
    parseFlightInfoField(
      (extraInfo as { bookingFields?: unknown } | null)?.bookingFields
    )
);

/**
 * Whether the entered flight info is valid for gating "Save and continue".
 * Compound field is Optional, so both-empty is valid; a partial fill (exactly
 * one of airline / flight number) is invalid; both-filled requires the flight
 * number to match its pattern. The airline is dropdown-controlled, so its value
 * is inherently valid and is NOT pattern-checked (the schema's letters-only
 * `^[A-Z]{2}$` would wrongly reject alphanumeric codes like B6 / F9). Returns
 * `true` when no flight-info field is requested (nothing to validate).
 */
export const getIsFlightInfoValid = createSelector(
  getFlightInfo,
  getFlightInfoFieldSpec,
  (flightInfo, spec): boolean => {
    if (!spec) return true;
    const airline = (flightInfo?.airlineCode ?? "").trim();
    const flightNumber = (flightInfo?.flightNumber ?? "").trim();
    const airlineFilled = airline.length > 0;
    const flightNumberFilled = flightNumber.length > 0;
    if (!airlineFilled && !flightNumberFilled) return true;
    if (airlineFilled !== flightNumberFilled) return false;
    return spec.flightNumber.pattern
      ? spec.flightNumber.pattern.test(flightNumber)
      : true;
  }
);
